import React, { useState, useEffect } from 'react';
import { Building, Plus, Eye, Edit, AlertTriangle, CheckCircle, TrendingUp, DollarSign, Download, Filter } from 'lucide-react';
import { StatusBadge } from '../../../../components/UI/StatusBadge';
import LoadingSpinner from '../../../../components/UI/LoadingSpinner';

const BudgetManagementTab = () => {
  const [departmentBudgets, setDepartmentBudgets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedYear, setSelectedYear] = useState(2025);

  useEffect(() => {
    loadDepartmentBudgets();
  }, [selectedYear]);

  const loadDepartmentBudgets = async () => {
    try {
      setLoading(true);
      // Mock data - replace with actual API call
      const mockBudgets = [
        {
          id: 1,
          department_name: 'Engineering',
          budget_year: 2025,
          allocated_budget: 50000,
          reserved_budget: 12000,
          utilized_budget: 8500,
          available_budget: 29500
        },
        {
          id: 2,
          department_name: 'Operations',
          budget_year: 2025,
          allocated_budget: 35000,
          reserved_budget: 8000,
          utilized_budget: 28000,
          available_budget: -1000
        },
        {
          id: 3,
          department_name: 'Finance',
          budget_year: 2025,
          allocated_budget: 25000,
          reserved_budget: 3000,
          utilized_budget: 12000,
          available_budget: 10000
        },
        {
          id: 4,
          department_name: 'HR',
          budget_year: 2025,
          allocated_budget: 20000,
          reserved_budget: 2000,
          utilized_budget: 17500,
          available_budget: 500
        }
      ];
      setDepartmentBudgets(mockBudgets);
    } catch (error) {
      console.error('Error loading department budgets:', error);
    } finally {
      setLoading(false);
    }
  };

  // Budget health thresholds (industry standard)
  // < 70% = Healthy (Green)
  // 70-90% = Caution (Yellow/Orange) 
  // > 90% = Critical (Red)
  const getBudgetHealth = (utilized, allocated) => {
    const percentage = (utilized / allocated) * 100;
    
    if (percentage >= 90) {
      return {
        status: 'failed',
        label: 'Critical',
        description: 'Budget nearly exhausted',
        progressClass: 'bg-red-500',
        icon: AlertTriangle,
        bgClass: 'bg-red-50',
        borderClass: 'border-red-200'
      };
    } else if (percentage >= 70) {
      return {
        status: 'pending',
        label: 'Caution',
        description: 'Monitor spending closely',
        progressClass: 'bg-yellow-500',
        icon: TrendingUp,
        bgClass: 'bg-yellow-50',
        borderClass: 'border-yellow-200'
      };
    } else {
      return {
        status: 'approved',
        label: 'Healthy',
        description: 'Budget on track',
        progressClass: 'bg-green-500',
        icon: CheckCircle,
        bgClass: 'bg-green-50',
        borderClass: 'border-green-200'
      };
    }
  };

  // Calculate summary stats
  const totalAllocated = departmentBudgets.reduce((sum, b) => sum + b.allocated_budget, 0);
  const totalUtilized = departmentBudgets.reduce((sum, b) => sum + b.utilized_budget, 0);
  const totalReserved = departmentBudgets.reduce((sum, b) => sum + b.reserved_budget, 0);
  const totalAvailable = departmentBudgets.reduce((sum, b) => sum + b.available_budget, 0);
  const overallHealth = getBudgetHealth(totalUtilized, totalAllocated);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with Year Filter and Actions */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 sm:gap-4">
        <div className="min-w-0">
          <h2 className="text-lg sm:text-2xl font-bold text-gray-900 truncate">Department Budget Overview</h2>
          <p className="text-xs sm:text-sm text-gray-500 mt-1 truncate">Track training budget allocation and utilization by department</p>
        </div>
        <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
          <select
            value={selectedYear}
            onChange={(e) => setSelectedYear(Number(e.target.value))}
            className="select-admin"
          >
            <option value={2025}>FY 2025</option>
            <option value={2024}>FY 2024</option>
            <option value={2023}>FY 2023</option>
          </select>
          <button
            className="icon-button-admin"
            title="Export budget overview"
            aria-label="Export budget overview"
          >
            <Download className="w-4 h-4" />
          </button>
          <button className="btn btn-admin-primary w-11 h-11 min-h-0 max-h-11 p-0 gap-0 shrink-0 sm:w-auto sm:max-h-none sm:min-h-[44px] sm:h-auto sm:px-4 sm:py-2.5 sm:gap-2 self-center sm:self-auto" aria-label="Add Budget">
            <Plus className="w-4 h-4" />
            <span className="hidden sm:inline">Add Budget</span>
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
        <div className="bg-white rounded-2xl shadow-sm border p-4 sm:p-5">
          <div className="flex items-center">
            <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
              <DollarSign className="w-5 h-5 text-blue-600" />
            </div>
            <div className="ml-3 min-w-0">
              <p className="text-base sm:text-xl font-bold text-gray-900 truncate">S$ {totalAllocated.toLocaleString()}</p>
              <p className="text-xs text-gray-500">Total Allocated</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-2xl shadow-sm border p-4 sm:p-5">
          <div className="flex items-center">
            <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
              <CheckCircle className="w-5 h-5 text-green-600" />
            </div>
            <div className="ml-3 min-w-0">
              <p className="text-base sm:text-xl font-bold text-gray-900 truncate">S$ {totalUtilized.toLocaleString()}</p>
              <p className="text-xs text-gray-500">Total Utilized</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-2xl shadow-sm border p-4 sm:p-5">
          <div className="flex items-center">
            <div className="w-10 h-10 bg-yellow-100 rounded-full flex items-center justify-center flex-shrink-0">
              <TrendingUp className="w-5 h-5 text-yellow-600" />
            </div>
            <div className="ml-3 min-w-0">
              <p className="text-base sm:text-xl font-bold text-gray-900 truncate">S$ {totalReserved.toLocaleString()}</p>
              <p className="text-xs text-gray-500">Reserved (Pending)</p>
            </div>
          </div>
        </div>
        <div className={`rounded-2xl shadow-sm border p-4 sm:p-5 ${overallHealth.bgClass} ${overallHealth.borderClass}`}>
          <div className="flex items-center">
            <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
              overallHealth.status === 'approved' ? 'bg-green-200' :
              overallHealth.status === 'pending' ? 'bg-yellow-200' : 'bg-red-200'
            }`}>
              <overallHealth.icon className={`w-5 h-5 ${
                overallHealth.status === 'approved' ? 'text-green-700' :
                overallHealth.status === 'pending' ? 'text-yellow-700' : 'text-red-700'
              }`} />
            </div>
            <div className="ml-3 min-w-0">
              <p className="text-base sm:text-xl font-bold text-gray-900 truncate">{Math.round((totalUtilized / totalAllocated) * 100)}%</p>
              <p className="text-xs text-gray-500">Overall Utilization</p>
            </div>
          </div>
        </div>
      </div>

      {/* Budget Health Legend */}
      <div className="bg-gray-50 rounded-lg p-4">
        <h4 className="text-sm font-medium text-gray-700 mb-3">Budget Health Thresholds</h4>
        <div className="flex flex-wrap gap-6 text-sm">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-green-500"></div>
            <span className="text-gray-600">Healthy (&lt;70%)</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
            <span className="text-gray-600">Caution (70-90%)</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-red-500"></div>
            <span className="text-gray-600">Critical (&gt;90%)</span>
          </div>
          <div className="w-full sm:w-auto sm:ml-auto text-xs text-gray-500 flex items-center gap-1" title="Reserved budget is funds committed to approved training not yet completed">
            <span className="underline decoration-dotted cursor-help">What is Reserved?</span>
            <span>— Approved training awaiting completion</span>
          </div>
        </div>
      </div>

      {/* Department Budget Cards */}
      {departmentBudgets.length === 0 ? (
        <div className="card">
          <div className="card-content">
            <div className="text-center py-12">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <DollarSign className="w-8 h-8 text-gray-400" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">No Budget Allocations</h3>
              <p className="text-gray-500 text-sm mb-4">
                No department budgets have been allocated for FY {selectedYear}.
              </p>
              <button className="btn btn-admin-primary">
                <Plus className="w-4 h-4 mr-2" />
                Add First Budget
              </button>
            </div>
          </div>
        </div>
      ) : (
      <div className="grid grid-cols-1 gap-4">
        {departmentBudgets.map((budget) => {
          const percentage = Math.round((budget.utilized_budget / budget.allocated_budget) * 100);
          const health = getBudgetHealth(budget.utilized_budget, budget.allocated_budget);
          const HealthIcon = health.icon;
          
          return (
            <div key={budget.id} className={`card flex flex-col h-full ${health.status === 'failed' ? 'border-red-200 bg-red-50/30' : ''}`}>
              <div className="card-content flex flex-col flex-1">
                <div className="flex items-start justify-between gap-3 mb-4">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <Building className="w-5 h-5 text-gray-600" />
                    </div>
                    <div className="min-w-0">
                      <h3 className="text-lg font-semibold text-gray-900 truncate">{budget.department_name}</h3>
                      <p className="text-xs text-gray-500">FY {budget.budget_year}</p>
                    </div>
                  </div>
                  <div className="flex-shrink-0">
                    <StatusBadge status={health.status} label={health.label} showIcon />
                  </div>
                </div>

                {/* Budget Breakdown */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Allocated</p>
                    <p className="text-lg font-semibold text-gray-900">S$ {budget.allocated_budget.toLocaleString()}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Utilized</p>
                    <p className={`text-lg font-semibold ${health.status === 'critical' ? 'text-red-600' : 'text-gray-700'}`}>
                      S$ {budget.utilized_budget.toLocaleString()}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Reserved</p>
                    <p className="text-lg font-semibold text-yellow-600">S$ {budget.reserved_budget.toLocaleString()}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Available</p>
                    <p className={`text-lg font-semibold ${budget.available_budget < 0 ? 'text-red-600' : 'text-green-600'}`}>
                      S$ {budget.available_budget.toLocaleString()}
                    </p>
                  </div>
                </div>
                
                {/* Progress Bar with Health-Based Color */}
                <div className="mb-3">
                  <div className="flex justify-between items-center gap-2 mb-1">
                    <span className="text-xs font-medium text-gray-600 flex-shrink-0">{percentage}% utilized</span>
                    <span className="text-xs text-gray-500 truncate text-right">{health.description}</span>
                  </div>
                  <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                    <div 
                      className={`h-full rounded-full transition-all duration-500 ${health.progressClass}`}
                      style={{ width: `${Math.min(percentage, 100)}%` }}
                    ></div>
                  </div>
                </div>

                {/* Actions */}
                <div className="card-action-footer">
                  <button className="btn btn-admin-outline w-11 h-11 min-h-0 max-h-11 p-0 gap-0 shrink-0 sm:w-auto sm:max-h-none sm:min-h-[44px] sm:h-auto sm:px-4 sm:py-2.5 sm:gap-2 btn-sm" title="View Transactions" aria-label="View Transactions">
                    <Eye className="w-4 h-4" />
                    <span className="hidden sm:inline">View Transactions</span>
                  </button>
                  <button className="btn btn-admin-secondary btn-sm" title="Adjust Budget" aria-label="Adjust Budget">
                    <Edit className="w-4 h-4 sm:mr-2" />
                    <span className="hidden sm:inline">Adjust Budget</span>
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
      )}
    </div>
  );
};

export default BudgetManagementTab;
